import React, { useState } from "react";
import { Trash2, Database, RefreshCcw, FileMinus, Image, Info } from "lucide-react";

export default function MaintenancePage() {
  const [openPanels, setOpenPanels] = useState({});

  const togglePanel = (key) => {
    setOpenPanels((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const Section = ({ title, children, icon, id }) => (
    <div className="bg-white border rounded-lg shadow-sm mb-4 overflow-hidden">
      <button
        onClick={() => togglePanel(id)}
        className="w-full flex justify-between items-center p-3 bg-gray-100 border-b text-left font-medium text-gray-700"
      >
        <div className="flex items-center gap-2">
          {icon}
          {title}
        </div>
        <span className="text-gray-500">{openPanels[id] ? "−" : "+"}</span>
      </button>
      {openPanels[id] !== false && <div className="p-4">{children}</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <h1 className="text-xl font-semibold mb-2 text-gray-800">Maintenance</h1>
      <p className="text-sm text-gray-600 mb-6">
        Learn more about <span className="text-blue-600 underline">maintenance</span>
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Deleting guest customers */}
        <Section
          id="guest"
          title="Deleting guest customers"
          icon={<Trash2 size={18} />}
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600">Start date</label>
              <input type="date" className="w-full border rounded-md p-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm text-gray-600">End date</label>
              <input type="date" className="w-full border rounded-md p-2 text-sm" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm">
            <input type="checkbox" id="no-cart" className="accent-blue-600" />
            <label htmlFor="no-cart" className="text-gray-700">
              Only without shopping cart
            </label>
          </div>
          <button className="mt-4 bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-md text-sm">
            Delete
          </button>
        </Section>

        {/* Deleting old exported files */}
        <Section
          id="exported"
          title="Deleting old exported files"
          icon={<Trash2 size={18} />}
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600">Start date</label>
              <input type="date" className="w-full border rounded-md p-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm text-gray-600">End date</label>
              <input type="date" className="w-full border rounded-md p-2 text-sm" />
            </div>
          </div>
          <button className="mt-4 bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-md text-sm">
            Delete
          </button>
        </Section>

        {/* Deleting abandoned shopping carts */}
        <Section
          id="abandoned"
          title="Deleting abandoned shopping carts"
          icon={<Trash2 size={18} />}
        >
          <div>
            <label className="block text-sm text-gray-600">Created before</label>
            <input type="date" className="w-full border rounded-md p-2 text-sm" />
          </div>
          <button className="mt-4 bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-md text-sm">
            Delete
          </button>
        </Section>

        {/* Re-index database tables */}
        <Section
          id="reindex"
          title="Re-index database tables"
          icon={<RefreshCcw size={18} />}
        >
          <p className="text-sm text-gray-600 mb-3">
            Modifies existing tables by rebuilding the index. Automatic or manual
            statistics created in the table are not updated.
          </p>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-md text-sm">
            Re-index
          </button>
        </Section>

        {/* Database backups */}
        <Section id="backup" title="Database backups" icon={<Database size={18} />}>
          <p className="text-sm text-gray-600 mb-4">
            Backup functionality works only when your application is on the same
            server as the database.
          </p>

          <div className="overflow-x-auto border rounded-md mb-3">
            <table className="min-w-full text-sm text-left text-gray-700">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2">File Name</th>
                  <th className="px-3 py-2">File Size</th>
                  <th className="px-3 py-2">Download</th>
                  <th className="px-3 py-2">Restore</th>
                  <th className="px-3 py-2">Delete</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan="5" className="px-3 py-2 text-center text-gray-500">
                    No records
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-md text-sm">
            Backup now
          </button>
        </Section>

        {/* Delete already sent emails */}
        <Section
          id="emails"
          title="Delete already sent emails"
          icon={<Trash2 size={18} />}
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600">Start date</label>
              <input type="date" className="w-full border rounded-md p-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm text-gray-600">End date</label>
              <input type="date" className="w-full border rounded-md p-2 text-sm" />
            </div>
          </div>
          <button className="mt-4 bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-md text-sm">
            Delete
          </button>
        </Section>

        {/* Delete minification files */}
        <Section
          id="minification"
          title="Delete minification files"
          icon={<FileMinus size={18} />}
        >
          <p className="text-sm text-gray-600 mb-3">
            Clears the bundles directory.
          </p>
          <button className="bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-md text-sm">
            Delete
          </button>
        </Section>

        {/* Delete image thumbs */}
        <Section id="thumbs" title="Delete image thumbs" icon={<Image size={18} />}>
          <p className="text-sm text-gray-600">
            Delete image thumbs from the thumbs directory (except placeholder.txt).
          </p>
          <ul className="text-sm text-gray-700 mt-2 list-disc ml-4">
            <li>Total files: 106</li>
            <li>Total file size: 351 MB</li>
          </ul>
          <button className="mt-4 bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-md text-sm">
            Delete
          </button>
        </Section>

        {/* Shrink database */}
        <Section
          id="shrink"
          title="Shrink database"
          icon={<Info size={18} />}
        >
          <p className="text-sm text-gray-600 mb-2">
            admin.system.maintenance.shrinkdatabase.text
          </p>
          <button className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-1.5 rounded-md text-sm">
            admin.system.maintenance.shrinkdatabase
          </button>
        </Section>
      </div>
    </div>
  );
}
